import { useState, useEffect } from 'react';
import { useDataSource } from '@/contexts/DataSourceContext';
import { apiClient } from '@/services/apiClient';

export interface ManagerDashboardSummaryDto {
  currentWeek: {
    start: string;
    end: string;
  };
  teamThisWeek: {
    totalHours: number;
    billableHours: number;
    utilizationRate: number;
    trendPercentage: number;
    teamSize: number;
  };
  approvalsRequired: {
    pendingCount: number;
    overdueCount: number;
    pendingApprovals: PendingApprovalDto[];
  };
  teamPerformance: {
    utilizationRate: number;
    utilizationTarget: number;
    isAboveTarget: boolean;
    teamSize: number;
    trendPercentage: number;
  };
}

export interface PendingApprovalDto {
  timesheetId: string;
  employeeId: string;
  employeeName: string;
  weekEnding: string;
  submittedAt?: string;
  daysSinceSubmission: number;
  isOverdue: boolean;
}

export function useManagerDashboardSummary(screenId: string = 'dashboard') {
  const { getScreenMode } = useDataSource();
  const [summary, setSummary] = useState<ManagerDashboardSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const mode = getScreenMode(screenId);

  useEffect(() => {
    if (mode !== 'live') {
      setLoading(false);
      return;
    }

    const fetchSummary = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await apiClient.get<ManagerDashboardSummaryDto>('/dashboard/manager-summary');
        setSummary(data);
      } catch (err) {
        console.error('Error fetching manager dashboard summary:', err);
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [mode, screenId]);

  return { summary, loading, error, mode };
}
